import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { blogPosts as staticPosts, blogTags } from "../mock";
import { Search, Tag, ArrowUpRight, Clock, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogIndex = ({ embedded = false }) => {
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("All");
  const [dynamicPosts, setDynamicPosts] = useState([]);

  const loadDynamicPosts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/blog/posts`);
      const normalizedPosts = (r.data || []).map((p) => ({
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt,
        tags: p.tags || [],
        date: p.date || (p.created_at ? new Date(p.created_at).toLocaleDateString() : ""),
        readTime: p.read_time || "5 min read",
        cover: p.cover || "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&auto=format&fit=crop",
        content: p.content || [],
      }));
      setDynamicPosts(normalizedPosts);
    } catch {
      setDynamicPosts([]);
    }
  }, []);

  useEffect(() => {
    loadDynamicPosts();
  }, [loadDynamicPosts]);

  const allPosts = useMemo(() => {
    // Merge dynamic first, dedupe by slug in favor of dynamic
    const map = new Map();
    dynamicPosts.forEach((p) => map.set(p.slug, p));
    staticPosts.forEach((p) => { if (!map.has(p.slug)) map.set(p.slug, p); });
    return Array.from(map.values());
  }, [dynamicPosts]);

  const filtered = useMemo(() => {
    return allPosts.filter((p) => {
      const matchesTag = tag === "All" || p.tags.includes(tag);
      const query = q.trim().toLowerCase();
      const matchesQ = !query || p.title.toLowerCase().includes(query) || p.excerpt.toLowerCase().includes(query) || p.tags.some((t) => t.toLowerCase().includes(query));
      return matchesTag && matchesQ;
    });
  }, [allPosts, q, tag]);

  const featured = filtered[0];
  const rest = filtered.slice(1);
  const revealClass = embedded ? "reveal visible" : "reveal";

  return (
    <div>
      {!embedded && (
        <PageHero
          eyebrow="Blog"
          title="Data engineering write-ups from the trenches"
          subtitle="Field notes on Databricks, Snowflake, Microsoft Fabric, dbt, governance, performance and enterprise AI. Written from real engagements."
          breadcrumb={[{ label: "Home", to: "/" }, { label: "Blog" }]}
        />
      )}

      <section className="py-16 bg-white">
        <div className="container-x">
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder="Search articles, topics or platforms…"
                className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm outline-none focus:border-[#1E56E3] dark:bg-[#0B1B3B] dark:border-white/10 dark:text-white"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {blogTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag(t)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                    tag === t
                      ? "bg-[#1E56E3] text-white"
                      : "bg-[#F5F7FB] text-slate-600 hover:bg-[#EEF3FF] hover:text-[#1E56E3] dark:bg-white/5 dark:text-slate-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="mt-16 text-center text-slate-500">No articles match your search.</div>
          )}

          {featured && (
            <Link to={`/blog/${featured.slug}`} className={`${revealClass} group mt-12 grid lg:grid-cols-12 gap-8 items-center border border-slate-200 rounded-2xl overflow-hidden hover-lift bg-white`} data-testid="insights-featured-blog-card">
              <div className="lg:col-span-6 relative h-72 lg:h-full overflow-hidden">
                <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest text-white bg-[#1E56E3] px-3 py-1 rounded">FEATURED</span>
              </div>
              <div className="lg:col-span-6 p-8 md:p-10">
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <Calendar size={13} /> {featured.date}
                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                  <Clock size={13} /> {featured.readTime}
                </div>
                <h2 className="mt-4 text-2xl md:text-3xl font-bold text-[#0B1B3B] leading-snug group-hover:text-[#1E56E3] transition-colors">{featured.title}</h2>
                <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {featured.tags.map((t) => (
                    <span key={t} className="text-[11px] font-medium bg-[#EEF3FF] text-[#1E56E3] px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                      <Tag size={11} /> {t}
                    </span>
                  ))}
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1E56E3] group-hover:gap-3 transition-all">
                  Read Article <ArrowUpRight size={16} />
                </span>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className={`${revealClass} group rounded-2xl overflow-hidden bg-white border border-slate-200 hover-lift block`} data-testid={`insights-blog-card-${p.slug}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/70 to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <Calendar size={12} /> {p.date}
                      <span className="h-1 w-1 rounded-full bg-slate-400" />
                      <Clock size={12} /> {p.readTime}
                    </div>
                    <h3 className="mt-3 text-lg font-bold text-[#0B1B3B] leading-snug group-hover:text-[#1E56E3] transition-colors line-clamp-2">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-medium bg-[#EEF3FF] text-[#1E56E3] px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogIndex;
